import { getReportFooterText } from '../reportFooter';
import type { PersonLabels } from '@/hooks/usePersonLabels';
export interface AllocationSummaryMemberInfo {
  name: string;
  rank?: string | null;
  unit?: string | null;
  individualId?: string | null;
}

export interface AllocationSummaryItem {
  handReceipt: 'PHR' | 'THR' | 'PnP';
  lin?: string | null;
  nsn?: string | null;
  catalogId?: string | null;
  nomenclature: string;
  quantity?: number | null;
  unitCost?: number | null;
  issueDate?: string | null;
}

export interface AllocationSummaryCounts {
  total: number;
  phrCount: number;
  thrCount: number;
}

export interface AllocationSummaryTemplateOptions {
  generatedAt: Date;
  member: AllocationSummaryMemberInfo;
  items: AllocationSummaryItem[];
  counts: AllocationSummaryCounts;
  heading?: string;
  filtersSummary?: string;
  personLabels?: PersonLabels;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const resolveQuantityDisplay = (quantity: number | null | undefined, receipt: 'PHR' | 'THR' | 'PnP') => {
  if (typeof quantity === 'number' && Number.isFinite(quantity)) {
    if (quantity <= 0) {
      return '—';
    }
    return String(quantity);
  }

  return receipt === 'PHR' || receipt === 'PnP' ? '1' : '—';
};

const resolveQuantityValue = (quantity: number | null | undefined, receipt: 'PHR' | 'THR' | 'PnP') => {
  if (typeof quantity === 'number' && Number.isFinite(quantity)) {
    return quantity > 0 ? quantity : 0;
  }

  return receipt === 'PHR' || receipt === 'PnP' ? 1 : 0;
};

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const buildItemsTable = (items: AllocationSummaryItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="9" class="empty-row">
          No authorized or issued items matched the print criteria.
        </td>
      </tr>
    `;
  }

  const sortedItems = [...items].sort((a, b) => {
    if (a.handReceipt !== b.handReceipt) {
      return a.handReceipt === 'PHR' ? -1 : 1;
    }
    const nameA = a.nomenclature.toLowerCase();
    const nameB = b.nomenclature.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const resolveLineTotalDisplay = (item: AllocationSummaryItem): string => {
    const unitCost = item.unitCost;
    if (typeof unitCost !== 'number' || !Number.isFinite(unitCost)) {
      return '—';
    }
    const qty = resolveQuantityValue(item.quantity, item.handReceipt);
    return formatCurrency(unitCost * qty);
  };

  return sortedItems
    .map(
      (item, index) => `
        <tr>
          <td class="index">${index + 1}</td>
          <td class="hr">${sanitizeText(item.handReceipt)}</td>
          <td>${sanitizeText(item.lin ?? '—')}</td>
          <td class="nsn">${sanitizeText(item.nsn ?? '—')}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td class="center">${escapeHtml(resolveQuantityDisplay(item.quantity, item.handReceipt))}</td>
          <td class="numeric money">${formatCurrency(item.unitCost ?? null)}</td>
          <td class="numeric money">${resolveLineTotalDisplay(item)}</td>
          <td class="date">${sanitizeText(item.issueDate ?? '—')}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildAllocationSummaryReportHtml = ({
  generatedAt,
  member,
  items,
  counts,
  heading = 'Allocation Summary',
  filtersSummary,
  personLabels,
}: AllocationSummaryTemplateOptions): string => {
  const pl = personLabels ?? { label: 'Individual', labelPlural: 'Individuals', lower: 'individual', lowerPlural: 'individuals', article: 'an' as const, individualIdLabel: 'Individual ID', individualIdPrefix: 'INDV-' };
  const generatedLabel = formatDateTime(generatedAt);
  const memberName = sanitizeText(member.name);
  const reportHeading = escapeHtml(heading);
  const filtersLabel = filtersSummary ? escapeHtml(filtersSummary) : '';
  const unit = member.unit ? sanitizeText(member.unit) : '—';
  const individualId = member.individualId ? sanitizeText(member.individualId) : '—';
  const rank = member.rank ? sanitizeText(member.rank) : null;
  const nameAlreadyHasRank = rank && rank !== '—' && memberName.toUpperCase().startsWith(rank.toUpperCase());
  const memberHeading = rank && rank !== '—' && !nameAlreadyHasRank ? `${rank} ${memberName}` : memberName;

  const phrLabel = `${counts.phrCount}`;
  const thrLabel = `${counts.thrCount}`;
  const totalLabel = `${counts.total}`;

  const itemsTable = buildItemsTable(items);

  const { totalCost, hasAnyCost } = items.reduce(
    (acc, item) => {
      const unitCost = item.unitCost;
      if (typeof unitCost !== 'number' || !Number.isFinite(unitCost)) {
        return acc;
      }

      const qty = resolveQuantityValue(item.quantity, item.handReceipt);
      acc.hasAnyCost = true;
      acc.totalCost += unitCost * qty;
      return acc;
    },
    { totalCost: 0, hasAnyCost: false as boolean },
  );

  const totalCostLabel = formatCurrency(hasAnyCost ? totalCost : null);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${reportHeading} – ${memberName}</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 24px;
        margin-bottom: 24px;
      }

      .brand {
        font-size: 14px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #3a82f6;
        font-weight: 600;
      }

      .title {
        font-size: 28px;
        font-weight: 700;
        margin: 0;
      }

      .generated {
        margin-top: 4px;
        font-size: 14px;
        color: #4a5c72;
      }

      .filters-applied {
        margin-top: 4px;
        font-size: 12px;
        font-style: italic;
        color: #5c6f85;
      }

      .meta-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 16px;
        margin-bottom: 24px;
      }

      .meta-block {
        padding: 12px 16px;
        border: 1px solid #e1e8f5;
        border-radius: 8px;
        background: #f8fbff;
      }

      .meta-label {
        display: block;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 4px;
      }

      .meta-value {
        font-size: 16px;
        font-weight: 600;
        color: #15263c;
      }

      .meta-subvalue {
        display: block;
        margin-top: 2px;
        font-size: 13px;
        font-weight: 500;
        color: #4a5c72;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 24px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .index {
        width: 32px;
        text-align: center;
        font-weight: 600;
        color: #4a5c72;
      }

      .numeric {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
      .money {
        white-space: nowrap;
      }
      .center {
        text-align: center;
      }
      .nsn {
        white-space: nowrap;
      }

      .hr {
        white-space: nowrap;
      }

      .date {
        white-space: nowrap;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      .totals-row td {
        background: #e9f1ff;
        font-weight: 700;
        border-top: 1px solid #d7e3f4;
      }

      .totals-label {
        text-align: right;
        color: #4a5c72;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        font-size: 12px;
      }

      .summary {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
      }

      .summary-chip {
        padding: 10px 16px;
        border-radius: 999px;
        background: #0d47a1;
        color: #ffffff;
        font-weight: 600;
        font-size: 13px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }

      footer {
        margin-top: 32px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @page {
        size: landscape;
        margin: 20mm;
      }

      @media print {
        body {
          margin: 0;
        }

        header {
          margin-top: 12px;
        }

        footer {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background-color: white;
          padding-top: 4mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <div>
        <div class="brand">eQuip Transaction Suite</div>
        <h1 class="title">${reportHeading}</h1>
        <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>${
          filtersLabel
            ? `
        <div class="filters-applied">${filtersLabel}</div>`
            : ''
        }
      </div>
      <div class="summary">
        <span class="summary-chip">Total Items: ${escapeHtml(totalLabel)}</span>
        <span class="summary-chip">PHR Items: ${escapeHtml(phrLabel)}</span>
        <span class="summary-chip">THR Items: ${escapeHtml(thrLabel)}</span>
      </div>
    </header>

    <section class="meta-grid">
      <div class="meta-block">
        <span class="meta-label">${pl.label}</span>
        <span class="meta-value">${memberHeading}</span>
        <span class="meta-subvalue">${pl.individualIdLabel ?? 'Individual ID'}: ${individualId}</span>
      </div>
      <div class="meta-block">
        <span class="meta-label">Unit</span>
        <span class="meta-value">${unit}</span>
      </div>
    </section>

    <section>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>HR</th>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th class="center">Quantity</th>
            <th class="numeric">Unit Cost</th>
            <th class="numeric">Total Cost</th>
            <th>Date Issued</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
          <tr class="totals-row">
            <td colspan="7" class="totals-label">Total Cost</td>
            <td class="numeric money">${totalCostLabel}</td>
            <td></td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="9" style="height: 15mm; border: none;"></td>
          </tr>
        </tfoot>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>

    <script>
      window.addEventListener('load', () => {
        window.focus();
        window.print();
        // setTimeout(() => window.close(), 400); // Optional auto-close
      });
    </script>
  </body>
</html>`;
};
